import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import axios from 'axios'
import {AuthAuroraShell, AuthBrandHeader} from './AuthAurora'
import {TURNSTILE_FAILURE_MESSAGE, useTurnstile} from '../core/useTurnstile'

const clientPortalAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {'Content-Type': 'application/json'},
})

const GENERIC_SUCCESS =
  'Se houver uma conta com esse e-mail, enviamos o link para redefinir a senha.'

const schema = Yup.object().shape({
  email: Yup.string().email('Formato de email inválido').required('Email é obrigatório'),
})

export function ClientPortalForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const {containerRef: turnstileRef, getToken: getCaptchaToken} = useTurnstile(
    'client_portal_forgot_password'
  )

  const formik = useFormik({
    initialValues: {email: ''},
    validationSchema: schema,
    onSubmit: async (values, {setSubmitting}) => {
      setLoading(true)
      setErrorMessage('')
      setSuccessMessage('')
      let captchaToken: string
      try {
        captchaToken = await getCaptchaToken()
      } catch {
        setErrorMessage(TURNSTILE_FAILURE_MESSAGE)
        setLoading(false)
        setSubmitting(false)
        return
      }

      try {
        const {data} = await clientPortalAxios.post('/api/client-portal/auth/forgot-password', {
          email: values.email,
          captchaToken,
        })
        setSuccessMessage(data?.message || GENERIC_SUCCESS)
      } catch (error: any) {
        const status = error.response?.status
        setErrorMessage(
          status === 422 || status === 429
            ? error.response?.data?.message || 'Não foi possível enviar o link. Tente novamente.'
            : 'Não foi possível enviar o link. Tente novamente.'
        )
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  return (
    <AuthAuroraShell>
      <AuthBrandHeader badge='Portal do Cliente' />

      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_client_portal_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-8'>
          <h1 className='text-dark fw-bolder mb-3'>Esqueci minha senha</h1>
          <div className='text-gray-500 fw-semibold fs-6'>
            Digite seu e-mail para redefinir sua senha
          </div>
        </div>

        {errorMessage && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{errorMessage}</div>
          </div>
        )}

        {successMessage && (
          <div className='mb-10 bg-light-info p-8 rounded'>
            <div className='text-info'>{successMessage}</div>
          </div>
        )}

        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-gray-900 fs-6' htmlFor='client-portal-forgot-email'>
            Email
          </label>
          <input
            id='client-portal-forgot-email'
            type='email'
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {'is-valid': formik.touched.email && !formik.errors.email}
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>

        {/* Turnstile: só aparece quando a Cloudflare pede interação */}
        <div ref={turnstileRef} className='d-flex justify-content-center mb-4' />

        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <button
            type='submit'
            id='kt_client_portal_password_reset_submit'
            className='btn btn-primary me-4'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            <span className='indicator-label'>Enviar</span>
            {loading && (
              <span className='indicator-progress'>
                Aguarde...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/auth/area-do-cliente/login'>
            <button type='button' className='btn btn-light'>
              Cancelar
            </button>
          </Link>
        </div>
      </form>
    </AuthAuroraShell>
  )
}
